import {
  createConsoleLogger,
  getClient,
  LogLevel,
  PollingMode,
  type IConfigCatClient,
} from '@configcat/sdk/browser';

// `undefined` = not yet resolved; `null` = resolved to "there is no client".
let client: IConfigCatClient | null | undefined;

// The ConfigCat client for this page, or null when no SDK key was built in
// (ADR-045). Null is a normal state, not an error: CI builds without a key on
// purpose, and so does a local dev server, so every flag resolves to its code
// default and the form renders exactly as deployed.
//
// A ConfigCat client-side SDK key is public by design — read-only, and served
// to every visitor — which is why it can live in a VITE_ variable on a page
// served to anonymous supporters.
export function getFlagClient(): IConfigCatClient | null {
  if (client !== undefined) {
    return client;
  }
  const sdkKey = (import.meta.env.VITE_CONFIGCAT_SDK_KEY as string | undefined)?.trim();
  if (!sdkKey) {
    client = null;
    return client;
  }
  client = getClient(sdkKey, PollingMode.LazyLoad, {
    // LazyLoad, NOT the AutoPoll the admin app uses. AutoPoll earns its keep
    // where something re-reads the polled value — an admin session lasts hours,
    // so a kill switch flipped mid-session should reach it without a reload.
    // Nothing re-reads here: FeatureFlagProvider evaluates once on mount with an
    // empty dependency array and never subscribes to config changes, so an
    // AutoPoll fetch every 60s would be network this page pays for and never
    // looks at — on a donation form a supporter may well be loading over mobile
    // data. LazyLoad fetches once, when the provider asks.
    //
    // The TTL is therefore close to moot (a second call would have to happen for
    // it to bind at all); 60s matches the Node client so the two do not differ
    // for no reason. If a live kill switch is ever wanted on this page, the fix
    // is to make the provider re-read — changing this line alone would not do it.
    cacheTimeToLiveSeconds: 60,
    requestTimeoutMs: 5000,
    logger: createConsoleLogger(LogLevel.Error),
  });
  return client;
}

export function __resetFlagClientForTests(): void {
  client = undefined;
}
